import type { Category, SpaceTemplateBundle, TaskTemplate } from "../../domain/entities.ts";
import { parsePortableSpaceTemplate, portableSpaceTemplate } from "./space-template-bundle.ts";
import { spaceTemplatesView, spaceTemplatePreview, spaceTemplateKey } from "./space-template-view.ts";
import { createDialogOperations } from "../../ui/dialog-operations.ts";
import { bindActions } from "../../app/actions.ts";

export interface SpaceTemplate {
  id: string;
  name: string;
  collections: SpaceTemplateBundle["collections"];
  taskTemplates: Array<Omit<TaskTemplate, "id" | "rootId" | "createdAt" | "updatedAt">>;
}

export interface SpaceTemplateCapabilities {
  document: Document;
  templates: () => SpaceTemplate[];
  setTemplates: (templates: SpaceTemplate[]) => Promise<void>;
  sharedTemplates: () => SpaceTemplateBundle[];
  canShare: () => boolean;
  saveShared: (bundle: Omit<SpaceTemplateBundle, "id" | "rootId" | "createdAt" | "updatedAt">) => Promise<void>;
  deleteShared: (bundle: SpaceTemplateBundle) => Promise<void>;
  activeRoot: () => string | null;
  activeRootName: () => string;
  categories: () => Category[];
  taskTemplates: () => TaskTemplate[];
  promptName: (title: string, value: string, confirmLabel: string) => Promise<string | null>;
  confirm?: (options: { title: string; message: string; confirmLabel: string; danger?: boolean }) => Promise<boolean>;
  createSpace: (id: string, name: string) => Promise<boolean>;
  createCollection: (rootId: string, collection: SpaceTemplate["collections"][number]) => Promise<string>;
  createTaskTemplate: (rootId: string, template: SpaceTemplate["taskTemplates"][number]) => Promise<void>;
  selectSpace: (rootId: string) => Promise<void>;
  renderShell: () => Promise<void>;
  markLocal: () => void;
  sync: () => void;
  randomId: () => string;
  icon: (name: string) => string;
  toast: (message: string, error?: boolean) => void;
}

export function createSpaceTemplateController(capabilities: SpaceTemplateCapabilities) {
  function snapshot(name: string): SpaceTemplate {
    const categories = capabilities.categories();
    const categoryIds = new Set(categories.map(category => category.id));
    return {
      id: capabilities.randomId(),
      name,
      collections: categories.map(({ id, customFieldIds, customFieldSchema, name, viewType, boardColumns, boardWipLimits, boardColumnSettings, boardCardFields, boardAutomation, sortBy, sortDirection, pageSize, showCompleted, defaultPriority, defaultTags, defaultTaskType, defaultDueOffsetDays }) => structuredClone({ sourceId: id, customFieldIds, customFieldSchema, name, viewType, boardColumns, boardWipLimits, boardColumnSettings, boardCardFields, boardAutomation, sortBy, sortDirection, pageSize, showCompleted, defaultPriority, defaultTags, defaultTaskType, defaultDueOffsetDays })),
      taskTemplates: capabilities.taskTemplates().map(({ id: _id, rootId: _rootId, createdAt: _createdAt, updatedAt: _updatedAt, ...template }) => structuredClone({ ...template, categoryId: template.categoryId && categoryIds.has(template.categoryId) ? template.categoryId : null })),
    };
  }

  function open(): void {
    const dialog = capabilities.document.createElement("dialog");
    dialog.className = "name-dialog management-dialog";
    dialog.setAttribute("aria-label", "Space templates");
    const operations = createDialogOperations(dialog, capabilities.toast);
    const opener = capabilities.document.activeElement as HTMLElement | null;
    let query = "", selectedKey: string | null = null;
    const render = () => {
      const local = capabilities.templates().map(template => ({ ...template, scope: "local" as const, version: 1 }));
      const shared = capabilities.sharedTemplates().map(template => ({ ...template, scope: "shared" as const }));
      const templates = [...shared, ...local];
      const find = (key: string) => templates.find(item => `${item.scope}:${item.id}` === key);
      const scroll = dialog.scrollTop;
      operations.render(spaceTemplatesView(templates, { query, canSave: !!capabilities.activeRoot(), canShare: capabilities.canShare(), icon: capabilities.icon }));
      dialog.classList.add("space-template-dialog");
      const search = dialog.querySelector<HTMLInputElement>("[data-space-template-search]")!;
      const rows = Array.from(dialog.querySelectorAll<HTMLElement>("[data-template-key]"));
      const preview = dialog.querySelector<HTMLElement>(".space-template-preview")!;
      const showPreview = () => {
        const selected = templates.find(item => spaceTemplateKey(item) === selectedKey);
        preview.innerHTML = spaceTemplatePreview(selected, capabilities.icon); preview.hidden = !selected;
        dialog.querySelectorAll<HTMLElement>("[data-preview-space-template]").forEach(button => button.setAttribute("aria-expanded", String(button.dataset.previewSpaceTemplate === selectedKey)));
        preview.querySelector("[data-template-preview-close]")?.addEventListener("click", () => {
          const oldKey = selectedKey; selectedKey = null; showPreview();
          (Array.from(dialog.querySelectorAll<HTMLElement>("[data-preview-space-template]")).find(button => button.dataset.previewSpaceTemplate === oldKey && !button.closest("[hidden]")) || search).focus();
        });
      };
      const filter = () => {
        let count = 0;
        rows.forEach((row, index) => { row.hidden = !templates[index].name.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()); if (!row.hidden) count++; });
        (dialog.querySelector("[data-template-empty]") as HTMLElement).hidden = !rows.length || count > 0;
        if (selectedKey && !rows.some(row => !row.hidden && row.dataset.templateKey === selectedKey)) { selectedKey = null; showPreview(); }
      };
      search.addEventListener("input", () => { query = search.value; filter(); });
      dialog.querySelectorAll<HTMLElement>("[data-preview-space-template]").forEach(button => button.addEventListener("click", () => { selectedKey = button.dataset.previewSpaceTemplate!; showPreview(); const heading = preview.querySelector<HTMLElement>("h3")!; heading.tabIndex = -1; heading.focus(); preview.scrollIntoView({ block: "nearest" }); }));
      showPreview(); filter(); bindActions(dialog, () => {});
      dialog.querySelectorAll<HTMLElement>(".space-template-menu [role=menuitem]").forEach(button => button.addEventListener("click", () => { const menu = button.closest("details")!; menu.removeAttribute("open"); menu.querySelector<HTMLElement>("summary")?.focus({ preventScroll: true }); }));
      dialog.scrollTop = scroll;
      dialog.querySelector("[data-close]")!.addEventListener("click", () => dialog.close());
      operations.listen("[data-save-space-template]", "click", async () => {
        const origin = capabilities.activeRoot();
        const name = await capabilities.promptName("Save space template", `${capabilities.activeRootName()} template`, "Save template");
        if (!name) return;
        if (capabilities.activeRoot() !== origin) throw new Error("The active space changed. Reopen templates before saving its setup.");
        await capabilities.setTemplates([...capabilities.templates(), snapshot(name)]);
        render();
        operations.toast("Space template saved");
      });
      operations.listen("[data-save-shared]", "click", async () => {
        const origin = capabilities.activeRoot();
        const name = await capabilities.promptName("Save space template", `${capabilities.activeRootName()} template`, "Save template");
        if (!name) return;
        if (!capabilities.canShare()) throw new Error("Space editing access changed. Reopen templates to continue.");
        if (capabilities.activeRoot() !== origin) throw new Error("The active space changed. Reopen templates before saving its setup.");
        try {
          const { format: _format, ...bundle } = parsePortableSpaceTemplate(JSON.stringify(portableSpaceTemplate(snapshot(name))));
          await capabilities.saveShared({ ...bundle, familyId: capabilities.randomId(), version: 1, deleted: false });
          render(); operations.toast("Space template saved and queued to sync"); capabilities.sync();
        } catch (error) { operations.toast(error instanceof Error ? error.message : "Could not save template", true); }
      });
      operations.listen("[data-revise]", "click", async event => {
        const button = event.currentTarget as HTMLElement;
        const previous = capabilities.sharedTemplates().find(item => item.id === button.dataset.revise);
        if (!previous) throw new Error("This template is no longer available. Reopen templates to continue.");
        if (!capabilities.canShare()) throw new Error("Space editing access changed. Reopen templates to continue.");
        const latest = Math.max(...capabilities.sharedTemplates().filter(item => item.familyId === previous.familyId).map(item => item.version));
        try {
          const { format: _format, ...bundle } = parsePortableSpaceTemplate(JSON.stringify(portableSpaceTemplate(snapshot(previous.name))));
          await capabilities.saveShared({ ...bundle, familyId: previous.familyId, version: latest + 1, deleted: false });
          render(); operations.toast("New template version saved"); capabilities.sync();
        } catch (error) { operations.toast(error instanceof Error ? error.message : "Could not revise template", true); }
      });
      operations.listen("[data-delete-space-template]", "click", async event => {
        const button = event.currentTarget as HTMLElement;
        const template = find(button.dataset.deleteSpaceTemplate || ""); if (!template) return;
        if (template.scope === "shared" && !capabilities.canShare()) throw new Error("Shared-template editing access changed. Reopen templates to continue.");
        if (capabilities.confirm && !await capabilities.confirm({ title: `Delete ${template.name}?`, message: "Removes this template, not spaces already created from it. This cannot be undone.", confirmLabel: "Delete template", danger: true })) return;
        if (template.scope === "shared" && !capabilities.canShare()) throw new Error("Shared-template editing access changed. Reopen templates to continue.");
        if (template.scope === "local") await capabilities.setTemplates(capabilities.templates().filter(item => item.id !== template.id));
        else { await capabilities.deleteShared(template); capabilities.sync(); }
        render();
      });
      operations.listen("[data-use-space-template]", "click", async event => {
        const button = event.currentTarget as HTMLElement;
        const template = find(button.dataset.useSpaceTemplate || "");
        if (!template) return;
        const name = await capabilities.promptName("Create space from template", template.name.replace(/ template$/i, ""), "Create space");
        if (!name) return;
        const rootId = capabilities.randomId();
        operations.progress({ stage: "Creating space" });
        if (!await capabilities.createSpace(rootId, name)) return;
        const portable = portableSpaceTemplate(template);
        const categoryIds = new Map<string, string>();
        let saved = 0;
        try {
        operations.progress({ stage: "Creating collections", completed: 0, total: portable.collections.length });
        for (const collection of portable.collections) {
          const createdId = await capabilities.createCollection(rootId, collection);
          if (collection.sourceId) categoryIds.set(collection.sourceId, createdId);
          operations.progress({ stage: "Creating collections", completed: ++saved, total: portable.collections.length });
        }
        saved = 0;
        operations.progress({ stage: "Creating task templates", completed: 0, total: portable.taskTemplates.length });
        for (const taskTemplate of portable.taskTemplates) {
          await capabilities.createTaskTemplate(rootId, { ...taskTemplate, categoryId: taskTemplate.categoryId ? categoryIds.get(taskTemplate.categoryId) || null : null });
          operations.progress({ stage: "Creating task templates", completed: ++saved, total: portable.taskTemplates.length });
        }
        await capabilities.selectSpace(rootId);
        } catch (error) { capabilities.markLocal(); capabilities.sync(); throw new Error(`The space was created, but setup did not finish. Inspect your spaces before creating another copy. ${error instanceof Error ? error.message : "Check your connection."}`); }
        dialog.close();
        capabilities.markLocal();
        try { await capabilities.renderShell(); } catch { throw new Error("Space created, but the page could not refresh. Reload to check it; do not create the same space again."); }
        operations.toast("Space created from template");
        capabilities.sync();
      });
      dialog.querySelectorAll<HTMLElement>("[data-export]").forEach(button => button.addEventListener("click", () => {
        const template = find(button.dataset.export || ""); if (!template) return;
        const url = URL.createObjectURL(new Blob([JSON.stringify(portableSpaceTemplate(template), null, 2)], { type: "application/json" }));
        const link = capabilities.document.createElement("a"); link.href = url;
        link.download = `weirdo-space-template-${template.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}.json`;
        link.click(); setTimeout(() => URL.revokeObjectURL(url), 1000);
      }));
      const input = dialog.querySelector<HTMLInputElement>("[data-import-file]")!;
      dialog.querySelector("[data-import]")?.addEventListener("click", () => input.click());
      input.addEventListener("change", () => { void operations.run(async () => {
        const file = input.files?.[0]; if (!file) return;
        try {
          if (file.size > 180_000) throw new Error("Template file is too large");
          const bundle = parsePortableSpaceTemplate(await file.text());
          const name = await capabilities.promptName("Import space template", bundle.name, "Import template");
          if (!name || !capabilities.canShare()) return;
          const { format: _format, ...content } = bundle;
          await capabilities.saveShared({ ...content, name, familyId: capabilities.randomId(), version: 1, deleted: false });
          render(); operations.toast("Template imported and queued to sync"); capabilities.sync();
        } catch (error) { operations.toast(error instanceof Error ? error.message : "Could not import template", true); }
        finally { input.value = ""; }
      }); });
    };
    render();
    capabilities.document.body.append(dialog);
    dialog.addEventListener("close", () => { dialog.remove(); if (opener?.isConnected) opener.focus({ preventScroll: true }); });
    dialog.showModal();
  }

  return { open, snapshot };
}
